/**
 * SPCB Order Summary Block (Blocks-native)
 *
 * Registers a checkout block that renders the order summary list
 * with native Store API quantity updates.
 *
 * @package SuperPymeCheckoutBlocks
 * @since 1.14.0
 */

import blockMetadata from './block.json';
import OrderSummaryBlock from './components/OrderSummaryBlock';

const { createElement } = wp.element;
const { enableQuantitySelector } = window.spcbQuantityData || {};
const reactRoots = new Map();
const activeClass = 'spcb-order-summary-active';

/**
 * Wait for wc.blocksCheckout to be available
 * WooCommerce Blocks loads asynchronously
 */
function waitForBlocksCheckout(callback, maxAttempts = 50) {
    let attempts = 0;

    const check = () => {
        attempts++;
        if (window.wc?.blocksCheckout?.registerCheckoutBlock) {
            callback();
        } else if (attempts < maxAttempts) {
            setTimeout(check, 100);
        } else {
            console.warn('[SPCB Quantity] wc.blocksCheckout not available after', maxAttempts, 'attempts');
        }
    };

    check();
}

/**
 * Render a React component to a container (React 18 compatible)
 */
function renderToContainer(container, element) {
    if (wp.element.createRoot) {
        let root = reactRoots.get(container);
        if (!root) {
            root = wp.element.createRoot(container);
            reactRoots.set(container, root);
        }
        root.render(element);
    } else {
        wp.element.render(element, container);
    }
}

/**
 * Ensure the SPCB order summary block renders even if the template
 * doesn't include it (fallback to a runtime mount).
 */
function mountOrderSummaryFallback() {
    const contents = document.querySelectorAll('.wc-block-components-checkout-order-summary__content');
    if (!contents.length) {
        return false;
    }

    contents.forEach((content) => {
        const coreBlocks = content.querySelectorAll(
            '.wp-block-woocommerce-checkout-order-summary-cart-items-block'
        );
        coreBlocks.forEach((block) => {
            block.style.display = 'none';
            block.dataset.spcbHidden = 'true';
        });

        let container = content.querySelector('.wp-block-spcb-checkout-order-summary-cart-items-block');
        if (!container) {
            container = document.createElement('div');
            container.className = 'wp-block-spcb-checkout-order-summary-cart-items-block wc-block-components-totals-wrapper';
            const firstCoreBlock = coreBlocks[0];
            if (firstCoreBlock && firstCoreBlock.parentNode === content) {
                content.insertBefore(container, firstCoreBlock);
            } else {
                content.insertBefore(container, content.firstChild);
            }
        }

        if (container.dataset.spcbMounted === 'true') {
            return;
        }

        renderToContainer(
            container,
            createElement(OrderSummaryBlock, { className: 'spcb-order-summary-block' })
        );
        container.dataset.spcbMounted = 'true';
        window.spcbOrderSummaryMounted = true;
        document.body?.classList?.add(activeClass);
    });

    return true;
}

/**
 * Schedule a fallback mount if the checkout template doesn't include our block.
 */
function scheduleFallbackMount() {
    let observer;
    let debounceTimer;
    let attempts = 0;
    const maxAttempts = 20;

    const attemptMount = () => {
        if (window.spcbOrderSummaryMounted) {
            return;
        }
        if (mountOrderSummaryFallback()) {
            return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
            debounceTimer = setTimeout(attemptMount, 250);
        } else if (observer) {
            observer.disconnect();
        }
    };

    const trigger = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(attemptMount, 120);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', trigger, { once: true });
    } else {
        trigger();
    }

    observer = new MutationObserver(trigger);
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 12000);
}

/**
 * Register the SPCB checkout block
 */
function registerSPCBOrderSummaryBlock() {
    const registerCheckoutBlock = window.wc?.blocksCheckout?.registerCheckoutBlock;

    if (typeof registerCheckoutBlock !== 'function') {
        console.warn('[SPCB Quantity] registerCheckoutBlock not available');
        return;
    }

    registerCheckoutBlock({
        metadata: blockMetadata,
        component: () => createElement(OrderSummaryBlock, {
            className: 'spcb-order-summary-block'
        })
    });

    console.log('[SPCB Quantity] SPCB order summary block registered');
    scheduleFallbackMount();
}

/**
 * Initialize when enabled
 */
if (enableQuantitySelector !== false) {
    console.log('[SPCB Quantity] Initializing (blocks mode)...');
    document.documentElement?.classList?.add(activeClass);
    document.body?.classList?.add(activeClass);
    waitForBlocksCheckout(registerSPCBOrderSummaryBlock);
}
